import { CustomizableChartFeature, CustomStylingConfig, getConfig, getDefaultConfig, } from '@thoughtspot/custom-style-service';
import { create } from '@thoughtspot/logger';
import _ from 'lodash';
const logger = create('chart-theme-service');
const AXIS_MARGIN = 20;
export const LEGEND_FONT_FAMILY = ' Optimo-Plain, Helvetica Neue, Helvetica, Arial, sans-serif';
export const LEGEND_BASE_FONT_SIZE = 12;
export const CONTRAST_SETTINGS = {
    strokeWidth: 3,
    textOutline: 'white',
    fontWeight: 600,
    textShadow: '0px 0px 2px rgba(255, 255, 255, 0.6)',
};
const DATA_LABEL_Y_OFFSET = -5;
export class ChartThemeService {
    static getCustomStyleConfig() {
        let customStyleConfig = getConfig();
        if (_.isNil(customStyleConfig)) {
            logger.warn('Custom Styles not applied, using default style configurations');
            customStyleConfig = getDefaultConfig();
        }
        return customStyleConfig;
    }
    static getCustomStylingForChart(chartType) {
        const chartFontFace = ChartThemeService.getCustomStyleConfig().getChartFontFace(chartType);
        return chartFontFace.toStyle();
    }
    static updateCustomStylingForChart(chartType, enableContrastSettings) {
        let defaultChartFontStyle = ChartThemeService.getCustomStylingForChart(chartType);
        const defaultFontColor = CustomStylingConfig.getDefaultChartFontColor();
        defaultChartFontStyle =
            defaultChartFontStyle.color === defaultFontColor &&
                enableContrastSettings
                ? _.merge(defaultChartFontStyle, CONTRAST_SETTINGS)
                : defaultChartFontStyle;
        return defaultChartFontStyle;
    }
    static getDefaultTheme(flags = {}) {
        const customStyleConfig = ChartThemeService.getCustomStyleConfig();
        const pieChartFontStyle = customStyleConfig
            .getChartFontFace(CustomizableChartFeature.PIE_CHART)
            .toStyle();
        const tooltipFontStyle = customStyleConfig
            .getChartFontFace(CustomizableChartFeature.TOOLTIP)
            .toStyle();
        const tooltipStyle = tooltipFontStyle;
        tooltipStyle.padding = '15px';
        const xAxisLabelFontStyle = customStyleConfig
            .getChartFontFace(CustomizableChartFeature.X_AXIS_LABEL)
            .toStyle();
        const xAxisLabelStyle = xAxisLabelFontStyle;
        xAxisLabelStyle.WebkitFontSmoothing = 'antialiased';
        const xAxisTitleFontStyle = customStyleConfig
            .getChartFontFace(CustomizableChartFeature.X_AXIS_TITLE)
            .toStyle();
        const yAxisLabelFontStyle = customStyleConfig
            .getChartFontFace(CustomizableChartFeature.Y_AXIS_LABEL)
            .toStyle();
        const yAxisTitleFontStyle = customStyleConfig
            .getChartFontFace(CustomizableChartFeature.Y_AXIS_TITLE)
            .toStyle();
        const colorPalettes = customStyleConfig.getChartColorPalettes();
        const cssColorPalettes = colorPalettes.map((palette) => palette.getCssColors());
        const allColors = cssColorPalettes[0];
        const startingColorIndex = 3;
        const primaryColors = allColors.filter((col, i) => (i + startingColorIndex) % 5 === 0);
        const hoverBrightness = flags.e2eTest ? 0 : 0.16;
        return {
            chart: {
                alignTicks: false,
                spacingTop: 15,
                spacingLeft: 3,
                spacingRight: 3,
                spacingBottom: 0,
                backgroundColor: '',
                resetZoomButton: {
                    theme: {
                        style: {
                            display: 'none',
                        },
                    },
                },
            },
            primaryColors,
            allColors,
            credits: {
                enabled: false,
            },
            plotOptions: {
                area: {
                    dataLabels: {
                        enabled: true,
                        style: ChartThemeService.updateCustomStylingForChart(CustomizableChartFeature.AREA_CHART, flags.applyContrastForDataLabels),
                    },
                },
                arearange: {
                    dataLabels: {
                        enabled: true,
                        style: ChartThemeService.updateCustomStylingForChart(CustomizableChartFeature.AREA_CHART, flags.applyContrastForDataLabels),
                    },
                    fillOpacity: 0.2,
                },
                areasplinerange: {
                    dataLabels: {
                        enabled: true,
                        style: ChartThemeService.updateCustomStylingForChart(CustomizableChartFeature.AREA_CHART, flags.applyContrastForDataLabels),
                    },
                    fillOpacity: 0.2,
                },
                areaspline: {
                    fillOpacity: 0.2,
                    marker: {
                        enabled: false,
                    },
                    dataLabels: {
                        enabled: true,
                        style: ChartThemeService.updateCustomStylingForChart(CustomizableChartFeature.AREA_CHART, flags.applyContrastForDataLabels),
                    },
                },
                bar: {
                    dataLabels: {
                        enabled: true,
                        style: ChartThemeService.updateCustomStylingForChart(CustomizableChartFeature.BAR_CHART, flags.applyContrastForDataLabels),
                    },
                },
                column: {
                    states: {
                        hover: {
                            brightness: hoverBrightness,
                        },
                    },
                    dataLabels: {
                        enabled: true,
                        style: ChartThemeService.updateCustomStylingForChart(CustomizableChartFeature.COLUMN_CHART, flags.applyContrastForDataLabels),
                    },
                },
                line: {
                    lineWidth: 1.5,
                    marker: {
                        enabled: false,
                    },
                    dataLabels: {
                        enabled: true,
                        y: DATA_LABEL_Y_OFFSET,
                        style: ChartThemeService.updateCustomStylingForChart(CustomizableChartFeature.LINE_CHART, flags.applyContrastForDataLabels),
                    },
                },
                pie: {
                    cursor: 'pointer',
                    dataLabels: {
                        connectorWidth: 1,
                        style: pieChartFontStyle,
                    },
                    showInLegend: true,
                },
                funnel: {
                    dataLabels: {
                        connectorWidth: 0,
                        style: ChartThemeService.updateCustomStylingForChart(CustomizableChartFeature.PIE_CHART, flags.applyContrastForDataLabels),
                    },
                },
                scatter: {
                    marker: {
                        symbol: 'circle',
                        radius: 5,
                        lineWidth: 1,
                        enabled: true,
                        states: {
                            hover: {
                                enabled: false,
                            },
                        },
                    },
                    dataLabels: {
                        enabled: true,
                        y: DATA_LABEL_Y_OFFSET,
                        style: ChartThemeService.updateCustomStylingForChart(CustomizableChartFeature.SCATTER_CHART, flags.applyContrastForDataLabels),
                    },
                    series: {
                        marker: {
                            enabled: true,
                        },
                    },
                },
                bubble: {
                    marker: {
                        symbol: 'circle',
                        radius: 5,
                        lineWidth: 1,
                        enabled: true,
                        states: {
                            hover: {
                                enabled: true,
                            },
                        },
                    },
                    dataLabels: {
                        enabled: true,
                        style: ChartThemeService.updateCustomStylingForChart(CustomizableChartFeature.SCATTER_CHART, flags.applyContrastForDataLabels),
                    },
                    series: {
                        marker: {
                            enabled: true,
                        },
                    },
                },
                series: {
                    borderWidth: 0,
                    animation: false,
                    marker: {
                        enabled: false,
                        symbol: 'circle',
                        radius: 5,
                        lineWidth: 1,
                    },
                    dataGrouping: {
                        enabled: false,
                    },
                    cursor: 'pointer',
                    shadow: false,
                    zoneAxis: 'x',
                },
                waterfall: {
                    dataLabels: {
                        enabled: true,
                        style: ChartThemeService.updateCustomStylingForChart(CustomizableChartFeature.BAR_CHART, flags.applyContrastForDataLabels),
                    },
                },
            },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                borderWidth: 1,
                style: tooltipStyle,
                shadow: true,
            },
            xAxis: {
                gridLineColor: 'transparent',
                lineWidth: 0,
                tickWidth: 0,
                labels: {
                    style: xAxisLabelStyle,
                    y: 20,
                    autoRotationLimit: 60,
                    padding: 15,
                },
                title: {
                    useHTML: true,
                    reserveSpace: true,
                    style: xAxisTitleFontStyle,
                    margin: AXIS_MARGIN,
                },
                scrollbar: {
                    enabled: false,
                    barBackgroundColor: '#bfbfbf',
                    barBorderWidth: 0,
                    barBorderRadius: 3,
                    height: 10,
                    rifleColor: '#bfbfbf',
                    showFull: false,
                    buttonBackgroundColor: '#ffffff',
                    buttonArrowColor: '#ffffff',
                    buttonBorderColor: '#ffffff',
                    buttonBorderWidth: 0,
                    trackBackgroundColor: '#f7f7f7',
                    trackBorderWidth: 0,
                },
            },
            yAxis: {
                gridLineColor: '#e0e0e0',
                gridLineWidth: 0,
                gridLineDashStyle: 'Solid',
                minPadding: 0,
                tickColor: 'red',
                tickLength: 0,
                tickWidth: 0,
                tickPosition: 'outside',
                lineWidth: 0,
                labels: {
                    style: yAxisLabelFontStyle,
                },
                stackLabels: {
                    style: {
                        color: '#5e5e5e',
                        fontFamily: '"Optimo-Plain", "Helvetica Neue", Helvetica, Arial, sans-serif',
                    },
                },
                title: {
                    style: yAxisTitleFontStyle,
                    useHTML: true,
                    reserveSpace: true,
                },
            },
        };
    }
    static useDefaultTheme(Highcharts, flags) {
        const theme = ChartThemeService.getDefaultTheme(flags);
        Highcharts.theme = theme;
        Highcharts.setOptions(Highcharts.theme);
    }
}
export const onGridline = {
    gridLineColor: '#e6e6e6',
    gridLineDashStyle: 'solid',
    gridLineWidth: 1,
};
export const offGridline = {
    gridLineColor: '#e6e6e6',
    gridLineDashStyle: 'solid',
    gridLineWidth: 0,
};
export const chartScreen = {
    HD_MIN: 720,
    FHD_MIN: 1600,
    QHD_MIN: 1920,
    UHD_MIN: 2560,
};
export const responsiveLayoutDisabledSpacingBottom = 5;
//# sourceMappingURL=chart-theme-service.js.map